import { useWindowDimensions } from "react-native"

import { useSafeAreaInsets } from "react-native-safe-area-context"

import { useStatusBarCompensation } from "./useStatusBarCompensation"

/** Maximum width of the centered iPad-style web canvas. */
export const WEB_CANVAS_MAX_WIDTH = 720

/**
 * Hook that calculates web-specific layout constraints for the avatar overlay.
 * On web, the app is constrained to a centered iPad-style portrait container.
 * This hook provides the dimensions and boundaries of that constrained canvas.
 */
export function useWebLayoutConstraints() {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()

  // Maximum width for the web canvas (matches chat content max-width)
  // This creates an iPad-style centered layout on wider screens
  const MAX_WEB_CANVAS_WIDTH = WEB_CANVAS_MAX_WIDTH

  // Calculate the effective canvas dimensions
  const constrainedWidth = Math.min(screenWidth, MAX_WEB_CANVAS_WIDTH)
  const isConstrained = constrainedWidth < screenWidth

  // Calculate horizontal boundaries of the visible canvas
  const canvasLeft = (screenWidth - constrainedWidth) / 2
  const canvasRight = canvasLeft + constrainedWidth

  // Calculate vertical boundaries
  const contentWidth = screenWidth - insets.left - insets.right
  const contentHeight = screenHeight - effectiveTopInset - insets.bottom
  const canvasHeight = contentHeight

  return {
    // Dimensions
    constrainedWidth,
    canvasWidth: constrainedWidth,
    canvasHeight,
    screenWidth,
    screenHeight,

    // Boundaries
    canvasLeft,
    canvasRight,
    canvasTop: effectiveTopInset,
    canvasBottom: screenHeight - insets.bottom,

    // Content area (excluding insets)
    contentWidth,
    contentHeight,

    // Flags
    isConstrained,

    // Insets
    insets,
    effectiveTopInset,
  }
}
